import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, MapPin, Pencil, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  asignarNodoArbitraje,
  getArbitrajeCentrosCarga,
  getOpcionesNodoArbitraje,
  quitarNodoArbitraje,
  type ArbCentroCarga,
  type TipoPuntoCauchy,
} from "@/lib/api";

const llave = (c: { id_contraparte: number; centro_carga: string }) =>
  `${c.id_contraparte}|${c.centro_carga}`;

/**
 * Configuración → Centros de carga (arbitraje).
 *
 * La plantilla OCEvr1 que se sube en /arbitraje trae el centro de carga solo
 * por nombre ("CC A", "CC B"…). Aquí se ve cada contraparte + centro de carga
 * que se ha cargado y se le asigna el nodo (PML) o la zona de carga (PND) a la
 * que corresponde (etrm.arbitraje_centro_carga, mig 109).
 */
export function ArbitrajeCentrosCargaPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["arbitraje-centros-carga"], queryFn: getArbitrajeCentrosCarga });
  const [editando, setEditando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);
  const refrescar = () => qc.invalidateQueries({ queryKey: ["arbitraje-centros-carga"] });

  const quitar = useMutation({
    mutationFn: (c: ArbCentroCarga) => quitarNodoArbitraje(c),
    onSuccess: refrescar,
  });

  const items = q.data?.items ?? [];
  const sinNodo = items.filter((c) => !c.clave).length;

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-slate-500" /> Centros de carga (arbitraje)
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Contrapartes y centros de carga que se han subido en{" "}
          <Link to="/arbitraje" className="text-blue-600 hover:underline">Arbitraje</Link>. La
          plantilla de CENACE no dice a qué precio corresponde cada centro de carga: asígnale un{" "}
          <b>nodo (PML)</b> o una <b>zona de carga (PND)</b>.
        </p>
        <p className="text-xs text-slate-400 mt-1 max-w-3xl">
          El VaR DART de cada día de arbitraje se corre con el nodo asignado aquí; sus parámetros
          (LH, tipo de cambio, confianza) se ajustan en Configuración → Nodos Cauchy. Al asignar un
          nodo se calcula el VaR de los días de ese centro que aún no lo tienen.
        </p>
      </div>

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {aviso && (
        <div className={`rounded p-3 text-sm border ${aviso.ok
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-amber-50 border-amber-200 text-amber-800"}`}>
          {aviso.texto}
        </div>
      )}
      {quitar.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(quitar.error as Error).message}
        </div>
      )}
      {q.isSuccess && items.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          Todavía no se ha cargado ningún centro de carga en Arbitraje.
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2">Contraparte</th>
                <th className="px-3 py-2">Centro de carga</th>
                <th className="px-3 py-2">Sistema</th>
                <th className="px-3 py-2">Días cargados</th>
                <th className="px-3 py-2 text-right">MWh</th>
                <th className="px-3 py-2">Nodo / zona</th>
                <th className="px-3 py-2 w-16" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const k = llave(c);
                const ed = editando === k;
                return (
                  <tr key={k} className="border-t align-top">
                    <td className="px-3 py-2">
                      {c.contraparte}
                      {c.alias && <span className="text-slate-400"> ({c.alias})</span>}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800">{c.centro_carga}</td>
                    <td className="px-3 py-2">{c.sistema ?? "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {c.dias > 0 ? (
                        <>
                          {c.dias} día{c.dias === 1 ? "" : "s"}
                          <div className="text-slate-400">
                            {fechaDMA(c.desde)}{c.desde !== c.hasta ? ` – ${fechaDMA(c.hasta)}` : ""}
                          </div>
                        </>
                      ) : (
                        <span className="text-slate-400">Sin días cargados</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(c.mwh)}</td>
                    <td className="px-3 py-2">
                      {ed ? (
                        <EditorNodo
                          cc={c}
                          onListo={(r) => {
                            setEditando(null);
                            refrescar();
                            setAviso(r.var_error
                              ? { ok: false, texto: `${c.centro_carga}: nodo guardado, pero el VaR no se calculó: ${r.var_error}` }
                              : {
                                  ok: true,
                                  texto: `${c.centro_carga}: nodo ${r.clave} guardado` +
                                    (r.var_calculados
                                      ? ` · VaR calculado para ${r.var_calculados} día(s) que no lo tenían.`
                                      : "."),
                                });
                          }}
                          onCancelar={() => setEditando(null)}
                        />
                      ) : (
                        <NodoAsignado c={c} />
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {!ed && (
                        <>
                          <button type="button" title={c.clave ? "Cambiar nodo" : "Asignar nodo"}
                            onClick={() => setEditando(k)}
                            className="text-slate-400 hover:text-slate-700">
                            <Pencil className="w-4 h-4" />
                          </button>
                          {c.clave && (
                            <button type="button" title="Quitar asignación"
                              disabled={quitar.isPending}
                              onClick={() => {
                                if (window.confirm(`¿Quitar el nodo de ${c.centro_carga} (${c.contraparte})?`)) {
                                  quitar.mutate(c);
                                }
                              }}
                              className="ml-2 text-slate-300 hover:text-red-600 disabled:opacity-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-3 py-2 border-t text-xs text-slate-500">
            {items.length} centro(s) de carga ·{" "}
            {sinNodo === 0 ? "todos con nodo asignado" : `${sinNodo} sin nodo asignado`}
          </div>
        </div>
      )}
    </div>
  );
}

function NodoAsignado({ c }: { c: ArbCentroCarga }) {
  if (!c.clave || !c.tipo) {
    return (
      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700">
        Sin asignar
      </span>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-2">
        <BadgeTipo tipo={c.tipo} />
        <span className="font-mono text-xs text-slate-800">{c.nodo_sistema} · {c.clave}</span>
      </div>
      {(c.nodo_nombre || c.nodo_zona) && (
        <div className="text-xs text-slate-500 mt-0.5">
          {c.nodo_nombre}{c.nodo_zona ? ` · zona ${c.nodo_zona}` : ""}
        </div>
      )}
      {c.actualizado_por && (
        <div className="text-[11px] text-slate-400 mt-0.5">
          {c.actualizado_por}
          {c.actualizado_en && ` · ${new Date(c.actualizado_en).toLocaleDateString("es-MX", {
            day: "2-digit", month: "short", year: "numeric",
          })}`}
        </div>
      )}
    </div>
  );
}

function BadgeTipo({ tipo }: { tipo: TipoPuntoCauchy }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
      tipo === "zona" ? "bg-sky-50 text-sky-700" : "bg-violet-50 text-violet-700"
    }`}>
      {tipo === "zona" ? "Zona (PND)" : "Nodo (PML)"}
    </span>
  );
}

/** Zona = lista completa del sistema; nodo = buscador en el catálogo NodosP (como Nodos Cauchy). */
function EditorNodo({ cc, onListo, onCancelar }: {
  cc: ArbCentroCarga;
  onListo: (r: { clave: string; var_calculados: number; var_error: string | null }) => void;
  onCancelar: () => void;
}) {
  const [tipo, setTipo] = useState<TipoPuntoCauchy>(cc.tipo ?? "nodo");
  const [sistema, setSistema] = useState<string>(cc.nodo_sistema ?? cc.sistema ?? "SIN");
  const [clave, setClave] = useState(cc.clave ?? "");
  const [busqueda, setBusqueda] = useState("");

  // El buscador de nodos espera a que se deje de teclear.
  const [termino, setTermino] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTermino(busqueda.trim()), 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const opciones = useQuery({
    queryKey: ["arbitraje-cc-opciones", tipo, sistema, tipo === "nodo" ? termino : ""],
    queryFn: () => getOpcionesNodoArbitraje(tipo, sistema, tipo === "nodo" ? termino : ""),
    enabled: tipo === "zona" || termino.length >= 2,
    staleTime: 10 * 60 * 1000,
  });

  const reiniciar = () => { setClave(""); setBusqueda(""); setTermino(""); };

  const guardar = useMutation({
    mutationFn: () => asignarNodoArbitraje({
      id_contraparte: cc.id_contraparte, centro_carga: cc.centro_carga, tipo, sistema, clave,
    }),
    onSuccess: onListo,
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <select
          value={tipo}
          onChange={(e) => { setTipo(e.target.value as TipoPuntoCauchy); reiniciar(); }}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        >
          <option value="nodo">Nodo (PML)</option>
          <option value="zona">Zona de carga (PND)</option>
        </select>
        <select
          value={sistema}
          onChange={(e) => { setSistema(e.target.value); reiniciar(); }}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        >
          <option value="SIN">SIN</option>
          <option value="BCA">BCA</option>
          <option value="BCS">BCS</option>
        </select>

        {tipo === "zona" ? (
          <select
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            className="w-52 rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{opciones.isLoading ? "Cargando…" : "Elige una zona"}</option>
            {(opciones.data ?? []).map((o) => (
              <option key={o.clave} value={o.clave}>{o.clave}</option>
            ))}
          </select>
        ) : (
          <div className="relative">
            <input
              autoFocus
              value={clave || busqueda}
              onChange={(e) => { setClave(""); setBusqueda(e.target.value); }}
              placeholder="Clave o nombre del nodo"
              className="w-52 rounded border border-slate-300 px-2 py-1 text-sm"
            />
            {!clave && termino.length >= 2 && (
              <ul className="absolute z-10 mt-1 w-80 max-h-64 overflow-auto rounded border border-slate-200 bg-white shadow-lg text-xs">
                {opciones.isLoading && <li className="px-3 py-2 text-slate-400">Buscando…</li>}
                {opciones.data?.length === 0 && (
                  <li className="px-3 py-2 text-slate-400">Sin coincidencias en {sistema}.</li>
                )}
                {(opciones.data ?? []).map((o) => (
                  <li key={o.clave}>
                    <button
                      type="button"
                      onClick={() => setClave(o.clave)}
                      className="w-full text-left px-3 py-1.5 hover:bg-slate-50"
                    >
                      <span className="font-mono text-slate-800">{o.clave}</span>
                      <span className="text-slate-500"> · {o.nombre}</span>
                      {o.zona_carga && <div className="text-slate-400">{o.zona_carga}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button type="button" title="Guardar" onClick={() => guardar.mutate()}
          disabled={!clave || guardar.isPending}
          className="inline-flex items-center gap-1 rounded bg-slate-900 text-white px-2.5 py-1 text-sm hover:bg-slate-800 disabled:opacity-50">
          <Check className="w-4 h-4" /> {guardar.isPending ? "Guardando…" : "Guardar"}
        </button>
        <button type="button" title="Cancelar" onClick={onCancelar}
          className="inline-flex items-center gap-1 rounded border bg-white text-slate-600 px-2.5 py-1 text-sm hover:bg-slate-50">
          <X className="w-4 h-4" /> Cancelar
        </button>
      </div>
      {guardar.isError && <div className="text-xs text-red-600">{(guardar.error as Error).message}</div>}
    </div>
  );
}

function num(v: number): string {
  return v.toLocaleString("es-MX", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function fechaDMA(iso: string | null): string {
  if (!iso) return "";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}
